/* ============================================================
 * UniMicro Certificate Setup (Option A)
 * ============================================================
 *
 * New logic:
 *  - Only asks for UniMicro Environment (dropdown)
 *  - No client_id / client_secret – handled by certificate
 *  - Calls /unimicro/status to bootstrap:
 *        • Save environment
 *        • Generate certificate JWT
 *        • Exchange for access token
 *        • Fetch companyKey
 *  - Polls until access_token + company_key are ready
 *  - Modal cannot close while processing
 */

import { mapMutations, mapGetters } from 'vuex';
import Forms from '../../utils/forms/forms';
import modulesService from '../../services/modules';
import unimicroService from '../../services/unimicro';
import organizationSettingsService from '../../services/organizationSettings';

export default {
  data() {
    return {
      form: new Forms({}),
      loading: false,
      pollingInterval: null,
      statusLocked: false, 
      activationConfirmed: false,
    };
  },

  props: {
    module: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      settings: 'organizationSettings',
    }),
  },

  mounted() {
    this.loading = true;
    this.statusLocked = false;

    const fields = {};
    const fieldList = this.module.required_fields || this.module.fields || [];
    let hasOptions = false;

    // keep your existing dynamic form building logic
    for (let i = 0; i < fieldList.length; i++) {
      const field = fieldList[i];
      if (field.hidden) continue;

      const newField = {
        type: field.type || 'text',
        value: '',
        label: field.label || field.name,
        placeholder: field.placeholder || '',
        required: field.required || field.requred || false,
      };

      if (field.options) hasOptions = true;

      fields[field.name] = newField;
    }

    this.form = new Forms(fields);

    // populate dropdown
    if (hasOptions) {
      for (let i = 0; i < fieldList.length; i++) {
        const field = fieldList[i];
        if (field.hidden || !field.options) continue;

        const options = field.options;
        this.form.setOptions(
          field.name,
          Object.keys(options).map((k) => ({
            id: k,
            name: options[k],
          }))
        );
      }
    }

    organizationSettingsService
      .show()
      .finally(() => (this.loading = false));
  },

  beforeDestroy() {
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  },

  methods: {
    ...mapMutations({
      toggle: 'TOGGLE_MODAL',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
    }),

    /**
     * MAIN ENTRY:
     * User clicks SUBSCRIBE
     */
    authorize() {
      this.statusLocked = true;
      this.form.loading = true;

      const payload = Object.assign(
        { module_id: this.module.id },
        this.form.data()
      );

      modulesService
        .subscribe(this.module.id, payload)
        .then(() => {
          this.$message({
            showClose: true,
            message: 'Connecting to UniMicro…',
            type: 'info',
          });

          this.callStatus(payload.unimicro_environment);
        })
        .catch((err) => {
          this.statusLocked = false;

          // FIXED: no optional chaining
          let msg = 'Failed to start UniMicro setup. Please check your input.';
          if (err && err.data && err.data.message) {
            msg = err.data.message;
          }

          this.$message({
            showClose: true,
            message: msg,
            customClass: 'error',
          });

          this.form.recordErrors(err);
          this.form.loading = false;
        });
    },


    /**
     * CALL /unimicro/status (begin certificate bootstrap)
     */
    callStatus(environment) {
      unimicroService
        .status({ environment })
        .then((res) => {
          const data = res.data || {};

          if (!data.success) {
            this.statusLocked = false;
            this.unsubscribeAfterFailure('UniMicro setup failed, Please Follow the instructions.');
            return;
          }

          // If backend still preparing, begin polling
          if (!data.has_access_token || !data.has_company_key) {
            this.pollForCertificateReady(environment);
            return;
          }

          // DONE
          this.onSetupComplete();
        })
        .catch((err) => {
          console.error('[UniMicro] status error', err);
          this.statusLocked = false;
          this.unsubscribeAfterFailure(
            'Failed to communicate with UniMicro. Setup cancelled.'
          );
        });
    },

    /**
     * POLLING — wait for backend to finish certificate authentication
     */
    pollForCertificateReady(environment) {
      let attempts = 0;
      const maxAttempts = 20;
      const interval = 3000;

      this.$message({
        showClose: true,
        message: 'Verifying UniMicro setup…',
        type: 'info',
      });

      this.pollingInterval = setInterval(() => {
        attempts++;
        unimicroService
          .status({ environment })
          .then((res) => {
            const data = res.data || {};

            if (data.has_access_token && data.has_company_key) {
              clearInterval(this.pollingInterval);
              this.onSetupComplete();
            }

            if (attempts >= maxAttempts) {
              clearInterval(this.pollingInterval);
              this.statusLocked = false;

              this.unsubscribeAfterFailure(
                'UniMicro setup timed out. Please try again.'
              );
            }
          })
          .catch(() => {});
      }, interval);
    },

    /**
     * FINISH SETUP — save, reload settings, close modal
     */
    onSetupComplete() {
      this.form.loading = false;
      this.statusLocked = false;

      Promise.all([modulesService.items(), organizationSettingsService.show()]);

      this.$message({
        showClose: true,
        message: 'UniMicro connected successfully!',
        type: 'success',
      });

      this.toggle(false);
      this.unsetModalContent();
    },

    /**
     * CANCEL / FAILURE
     */
    unsubscribeAfterFailure(message) {
      if (this.pollingInterval) clearInterval(this.pollingInterval);

      modulesService
        .unsubscribe(this.module.id)
        .then(() => {
          this.$message({
            showClose: true,
            message,
            customClass: 'error',
          });
        })
        .finally(() => {
          this.form.loading = false;
          this.toggle(false);
          this.unsetModalContent();
        });
    },

    /**
     * Prevent closing during processing
     */
    closeModal(shouldUnsubscribe = true) {
      if (this.statusLocked) {
        this.$message({
          showClose: true,
          message: 'Please wait — UniMicro setup in progress…',
          type: 'warning',
        });
        return;
      }

      // original behavior
      this.form.loading = true;
      modulesService
        .unsubscribe(this.module.id)
        .finally(() => {
          this.form.loading = false;
          this.toggle(false);
          this.unsetModalContent();
        });
    },
  },

  components: {
    VForm: require('./../../components/form/form.vue'),
    VMessage: require('./../../components/form/message/message.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/unimicroModuleSetup/unimicroModuleSetup.js